/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Icon, IIconStyleProps, IIconStyles } from '@fluentui/react/lib/Icon';
import { Image } from '@fluentui/react/lib/Image';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logNextSteps } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getAssetsUrl } from 'src/core/utils/api/getAssetsUrl';
import { nextStepsDynamicCustomerServiceLink } from 'src/languageStudio/appShell/prebuiltDemos/utils/demosSupportLinks';

type NextStepsSasSolutionScreenProps = { isResultsEmpty: boolean };

const messages = defineMessages({
    title: { id: 'languageStudio.nextStepsSasSolutionScreen.title', defaultMessage: 'Dynamics 365 Customer Service' },
    dynamicsImageAlt: {
        id: 'languageStudio.nextStepsSasSolutionScreen.dynamicsImageAlt',
        defaultMessage: 'Dynamics 365 Customer Service image'
    },
    dynamicsLink: {
        id: 'languageStudio.nextStepsSasSolutionScreen.dynamicsLink',
        defaultMessage: 'Free trial of Dynamics 365 Custom Service'
    },
    readySolutionQuestion: {
        id: 'languageStudio.nextStepsSasSolutionScreen.readySolutionQuestion',
        defaultMessage: 'Looking for a ready made contact center solution?'
    },
    contactCenter: {
        id: 'languageStudio.nextStepsSasSolutionScreen.contactCenter',
        defaultMessage:
            'Set up your contact center in just a few clicks. Add onsite or remote agents and start engaging with your customers in minutes.'
    },
    readySolutionAnswer: {
        id: 'languageStudio.nextStepsSasSolutionScreen.readySolutionAnswer',
        defaultMessage: `Dynamics is built on the Microsoft cloud and empowers your teams with AI-enabled experiences to deliver the personalized
    service your customers expect. Create an omnichannel experience to deliver effortless and personalized customer
    experiences.`
    }
});

const iconStyles = classNamesFunction<IIconStyleProps, IIconStyles>()({ root: { fontSize: '14px' } });

export const NextStepsSasSolutionScreen = ({ isResultsEmpty }: NextStepsSasSolutionScreenProps) => {
    const { localize } = useLocalization();

    const handleClickLink = () => {
        if (!isResultsEmpty) {
            logNextSteps();
        }
    };

    return (
        <Stack horizontal tokens={{ childrenGap: 25 }}>
            <Stack horizontal tokens={{ childrenGap: 8 }}>
                <Image
                    alt={localize(messages.dynamicsImageAlt)}
                    src={getAssetsUrl('assets/images/languageStudio/dynamicsServiceLogo.svg')}
                />
                <LuisText styles={{ root: { maxWidth: '135px' } }}>{localize(messages.title)}</LuisText>
            </Stack>
            <Stack tokens={{ childrenGap: 15 }} styles={{ root: { flex: 1 } }}>
                <LuisText as="h3" fontWeight={600}>
                    {localize(messages.readySolutionQuestion)}
                </LuisText>
                <LuisText>{localize(messages.readySolutionAnswer)}</LuisText>
                <LuisText>{localize(messages.contactCenter)}</LuisText>
                <ExternalLink href={nextStepsDynamicCustomerServiceLink} onClick={handleClickLink}>
                    <Stack horizontal verticalAlign="baseline">
                        <LuisText>{localize(messages.dynamicsLink)}</LuisText>&nbsp;
                        <Icon iconName="MiniExpand" styles={iconStyles} />
                    </Stack>
                </ExternalLink>
            </Stack>
        </Stack>
    );
};
